import React, { useState, useEffect, useCallback, useMemo } from "react";
import { 
  Bell, 
  CheckCircle2, 
  AlertTriangle, 
  ShieldAlert, 
  Info, 
  ExternalLink,
  Search,
  Filter,
  CheckCheck,
  Layers,
  Clock,
  User,
  Sparkles,
  X,
  ChevronRight
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import Button from "../../components/common/Button";
import HeaderBar from "./components/headerBar";
import { 
  fetchNotifications, 
  markNotificationAsRead, 
  markAllNotificationsAsRead, 
  NotificationItem, 
  NotificationType 
} from "../../services/notificationService";

const getRelativeTime = (dateInput?: string | null): string => {
  if (!dateInput) return "";
  try {
    const now = new Date();
    const date = new Date(dateInput);
    const diffMs = now.getTime() - date.getTime();

    if (diffMs < 60000) return "just now";
    const diffMins = Math.round(diffMs / 60000);
    if (diffMins < 60) return `${diffMins}m ago`;
    const diffHours = Math.round(diffMs / 3600000);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.round(diffMs / 86400000);
    if (diffDays < 7) return `${diffDays}d ago`;

    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  } catch {
    return "";
  }
};

const getTypeConfig = (type: NotificationType | string) => {
  switch (type?.toUpperCase()) {
    case "SUCCESS":
      return {
        icon: <CheckCircle2 size={20} />,
        badgeBg: "cht-bg-emerald-100 cht-text-emerald-800",
        iconContainer: "cht-bg-emerald-100 cht-text-emerald-600",
        label: "Success",
      };
    case "WARNING":
      return {
        icon: <AlertTriangle size={20} />,
        badgeBg: "cht-bg-amber-100 cht-text-amber-800",
        iconContainer: "cht-bg-amber-100 cht-text-amber-600",
        label: "Warning",
      };
    case "ALERT":
      return {
        icon: <ShieldAlert size={20} />,
        badgeBg: "cht-bg-rose-100 cht-text-rose-800",
        iconContainer: "cht-bg-rose-100 cht-text-rose-600",
        label: "Alert",
      };
    case "INFO":
    default:
      return {
        icon: <Info size={20} />,
        badgeBg: "cht-bg-blue-100 cht-text-blue-800",
        iconContainer: "cht-bg-blue-100 cht-text-blue-600",
        label: "Information",
      };
  }
};

interface NotificationCardProps {
  item: NotificationItem;
  onMarkRead: (id: number) => void;
  onViewDetails: (item: NotificationItem) => void;
}

const NotificationCard: React.FC<NotificationCardProps> = ({ item, onMarkRead, onViewDetails }) => {
  const config = getTypeConfig(item.notification_type);
  const isUnread = !item.is_read;
  const timeLabel = getRelativeTime(item.created_at);

  return (
    <div
      onClick={() => onViewDetails(item)}
      className={`cht-group cht-flex cht-items-start cht-p-5 sm:cht-p-6 cht-rounded-[26px] cht-transition-all cht-duration-300 hover:cht-bg-gray-50/80 cht-relative cht-cursor-pointer cht-border ${
        isUnread
          ? "cht-bg-[#3e57da]/[0.03] cht-border-[#3e57da]/15 cht-shadow-sm"
          : "cht-bg-white cht-border-gray-100"
      }`}
    >
      {/* Type Icon */}
      <div
        className={`cht-w-11 cht-h-11 sm:cht-w-12 sm:cht-h-12 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-shrink-0 ${config.iconContainer} cht-shadow-sm`}
      >
        {config.icon}
      </div>

      {/* Details */}
      <div className="cht-ml-4 sm:cht-ml-5 cht-flex-1 cht-min-w-0">
        <div className="cht-flex cht-justify-between cht-items-start cht-gap-4">
          <div className="cht-space-y-1">
            <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2">
              <span
                className={`cht-px-2.5 cht-py-0.5 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-wider ${config.badgeBg}`}
              >
                {config.label}
              </span>
              {item.target_model && (
                <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2 cht-py-0.5 cht-bg-gray-100 cht-text-gray-700 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-wider">
                  <Layers size={10} />
                  {item.target_model}
                  {item.target_id ? ` #${item.target_id}` : ""}
                </span>
              )}
            </div>
            <h4 className="cht-text-base cht-font-black cht-text-gray-900 cht-leading-snug cht-pt-0.5">
              {item.title}
            </h4>
          </div>

          <div className="cht-flex cht-flex-col cht-items-end cht-gap-1.5 cht-shrink-0">
            <span className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
              {timeLabel}
            </span>
            {isUnread ? (
              <span className="cht-px-2 cht-py-0.5 cht-bg-[#3e57da] cht-text-white cht-text-[9px] cht-font-black cht-uppercase cht-tracking-wider cht-rounded-md cht-shadow-sm">
                New
              </span>
            ) : (
              <div className="cht-flex cht-items-center cht-gap-1 cht-text-[#3e57da]">
                <CheckCheck size={14} className="cht-stroke-[2.5]" />
                <span className="cht-text-[9px] cht-font-black cht-uppercase cht-tracking-wider">
                  Read
                </span>
              </div>
            )}
          </div>
        </div>

        <p className="cht-text-xs sm:cht-text-sm cht-text-gray-600 cht-mt-2 cht-font-medium cht-leading-relaxed cht-line-clamp-2">
          {item.message}
        </p>

        {/* Card Actions */}
        <div className="cht-mt-4 cht-flex cht-items-center cht-gap-3">
          <Button
            variant="ghost"
            size="sm"
            leftIcon={<ExternalLink size={13} />}
            className="cht-text-[#3e57da] hover:cht-bg-[#3e57da]/5 !cht-py-1.5 !cht-px-3 !cht-text-xs !cht-h-auto cht-rounded-xl"
            onClick={(e) => {
              e.stopPropagation();
              onViewDetails(item);
            }}
          >
            View Dossier
          </Button>

          {isUnread && (
            <Button
              variant="ghost"
              size="sm"
              className="cht-text-gray-400 hover:cht-text-gray-800 !cht-py-1.5 !cht-px-3 !cht-text-xs !cht-h-auto cht-rounded-xl"
              onClick={(e) => {
                e.stopPropagation();
                onMarkRead(item.id);
              }}
            >
              Mark as read
            </Button>
          )}

          {item.actor_name && (
            <span className="cht-ml-auto cht-text-[11px] cht-text-gray-400 cht-font-medium cht-hidden sm:cht-inline">
              Triggered by: <strong className="cht-text-gray-700">{item.actor_name}</strong>
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export const UniversalNotificationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [readFilter, setReadFilter] = useState<"ALL" | "UNREAD" | "READ">("ALL");
  const [typeFilter, setTypeFilter] = useState<"ALL" | NotificationType>("ALL");
  const [notifications, setNotifications] = useState<NotificationItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedNotification, setSelectedNotification] = useState<NotificationItem | null>(null);
  const [visibleCount, setVisibleCount] = useState(10);

  const loadNotifications = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await fetchNotifications({
        page: 1,
        page_size: 100, // Fetch recent activity pool
      });
      setNotifications(res.results || []);
    } catch (error) {
      console.error("Error loading universal notifications:", error);
      setNotifications([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleMarkRead = async (id: number) => {
    try {
      await markNotificationAsRead(id);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n))
      );
    } catch (error) {
      console.error("Error marking notification as read:", error);
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await markAllNotificationsAsRead();
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
    } catch (error) {
      console.error("Error marking all notifications as read:", error);
    }
  };

  const handleViewDetails = (item: NotificationItem) => {
    setSelectedNotification(item);
    if (!item.is_read) {
      handleMarkRead(item.id);
    }
  };

  // Filtered dataset
  const filteredNotifications = useMemo(() => {
    return notifications.filter((notif) => {
      const matchesSearch =
        notif.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notif.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (notif.target_model && notif.target_model.toLowerCase().includes(searchTerm.toLowerCase()));

      let matchesRead = true;
      if (readFilter === "UNREAD") matchesRead = !notif.is_read;
      if (readFilter === "READ") matchesRead = notif.is_read;

      const matchesType = typeFilter === "ALL" || notif.notification_type === typeFilter;

      return matchesSearch && matchesRead && matchesType;
    });
  }, [notifications, searchTerm, readFilter, typeFilter]);

  const unreadCount = useMemo(() => {
    return notifications.filter((n) => !n.is_read).length;
  }, [notifications]);

  const typeTabs: { id: "ALL" | NotificationType; label: string; count: number }[] = [
    { id: "ALL", label: "All Types", count: notifications.length },
    { id: "INFO", label: "Information", count: notifications.filter((n) => n.notification_type === "INFO").length },
    { id: "SUCCESS", label: "Success", count: notifications.filter((n) => n.notification_type === "SUCCESS").length },
    { id: "WARNING", label: "Warnings", count: notifications.filter((n) => n.notification_type === "WARNING").length },
    { id: "ALERT", label: "Alerts", count: notifications.filter((n) => n.notification_type === "ALERT").length },
  ];

  return (
    <section className="cht-animate-in cht-fade-in cht-duration-700 cht-space-y-8">
      {/* Header Bar */}
      <HeaderBar pageHeader="Notification & Activity Center" />

      <main className="cht-container cht-pb-20">
        <div className="cht-flex cht-flex-col lg:cht-flex-row cht-gap-8">
          {/* Main Content Area */}
          <div className="cht-flex-1 cht-space-y-6">
            {/* Search & Read Status Bar */}
            <div className="cht-bg-white cht-p-4 cht-rounded-[26px] cht-border cht-border-gray-100 cht-shadow-sm cht-flex cht-flex-col sm:cht-flex-row cht-gap-4 cht-items-center cht-justify-between">
              {/* Search */}
              <div className="cht-relative cht-w-full sm:cht-w-80">
                <Search
                  className="cht-absolute cht-left-4 cht-top-1/2 -cht-translate-y-1/2 cht-text-gray-400"
                  size={17}
                />
                <input
                  type="text"
                  placeholder="Search notifications, models, or topics..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="cht-w-full cht-bg-gray-50 cht-border-none cht-rounded-xl cht-py-3 cht-pl-11 cht-pr-4 cht-text-xs cht-font-bold cht-outline-none focus:cht-ring-2 focus:cht-ring-[#3e57da]/10 transition-all"
                />
              </div>

              {/* Status Pill Filters & Actions */}
              <div className="cht-flex cht-flex-wrap cht-items-center cht-gap-2 cht-w-full sm:cht-w-auto">
                <div className="cht-flex cht-items-center cht-gap-1 cht-bg-gray-100 cht-p-1 cht-rounded-xl">
                  {(["ALL", "UNREAD", "READ"] as const).map((mode) => (
                    <button
                      key={mode}
                      onClick={() => setReadFilter(mode)}
                      className={`cht-px-3 cht-py-1.5 cht-rounded-lg cht-text-[11px] cht-font-black cht-uppercase cht-tracking-wider cht-transition-all ${
                        readFilter === mode
                          ? "cht-bg-white cht-text-[#1e293b] cht-shadow-sm"
                          : "cht-text-gray-500 hover:cht-text-gray-800"
                      }`}
                    >
                      {mode}
                    </button>
                  ))}
                </div>

                {unreadCount > 0 && (
                  <Button
                    variant="primary"
                    size="sm"
                    leftIcon={<CheckCheck size={14} />}
                    onClick={handleMarkAllRead}
                    className="cht-rounded-xl"
                  >
                    Mark All Read
                  </Button>
                )}
              </div>
            </div>

            {/* Notification Cards Deck */}
            <div className="cht-bg-white cht-rounded-[32px] cht-border cht-border-gray-100 cht-shadow-[0_20px_60px_rgba(0,0,0,0.02)] cht-overflow-hidden">
              <div className="cht-p-3 sm:cht-p-4 cht-space-y-3">
                {isLoading ? (
                  <div className="cht-py-20 cht-text-center cht-space-y-3">
                    <div className="cht-w-8 cht-h-8 cht-border-2 cht-border-[#3e57da] cht-border-t-transparent cht-rounded-full cht-animate-spin cht-mx-auto" />
                    <p className="cht-text-xs cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                      Retrieving notifications...
                    </p>
                  </div>
                ) : filteredNotifications.length > 0 ? (
                  filteredNotifications.slice(0, visibleCount).map((item) => (
                    <NotificationCard
                      key={item.id}
                      item={item}
                      onMarkRead={handleMarkRead}
                      onViewDetails={handleViewDetails}
                    />
                  ))
                ) : (
                  <div className="cht-py-20 cht-text-center cht-space-y-3">
                    <div className="cht-w-16 cht-h-16 cht-bg-gray-50 cht-rounded-2xl cht-flex cht-items-center cht-justify-center cht-mx-auto cht-text-gray-300">
                      <Bell size={28} />
                    </div>
                    <h3 className="cht-text-base cht-font-black cht-text-gray-800">
                      No notifications found
                    </h3>
                    <p className="cht-text-xs cht-text-gray-400">
                      {searchTerm || readFilter !== "ALL" || typeFilter !== "ALL"
                        ? "Try adjusting or clearing your filters."
                        : "You are completely up to date with platform activity."}
                    </p>
                  </div>
                )}
              </div>

              {/* Load More */}
              {visibleCount < filteredNotifications.length && (
                <div className="cht-p-6 cht-bg-gray-50/50 cht-text-center cht-border-t cht-border-gray-100 cht-flex cht-justify-center">
                  <Button
                    variant="ghost"
                    size="sm"
                    leftIcon={<ChevronRight size={14} />}
                    className="cht-text-[#3e57da] hover:cht-text-[#3e57da] cht-font-black cht-text-xs"
                    onClick={() => setVisibleCount((prev) => prev + 10)}
                  >
                    Load More Activity ({filteredNotifications.length - visibleCount} remaining)
                  </Button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Classification & Quick Overview */}
          <aside className="lg:cht-w-[300px] cht-space-y-6">
            <div className="cht-bg-white cht-p-6 cht-rounded-[28px] cht-border cht-border-gray-100 cht-shadow-sm">
              <h4 className="cht-text-[11px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest cht-mb-4">
                Classifications
              </h4>
              <nav className="cht-space-y-1.5">
                {typeTabs.map((tab) => {
                  const isActive = typeFilter === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => setTypeFilter(tab.id)}
                      className={`cht-w-full cht-flex cht-items-center cht-justify-between cht-px-4 cht-py-2.5 cht-rounded-xl cht-text-xs cht-font-bold cht-transition-all ${
                        isActive
                          ? "cht-bg-[#1e293b] cht-text-white cht-shadow-sm"
                          : "cht-bg-gray-50/70 cht-text-gray-600 hover:cht-bg-gray-100"
                      }`}
                    >
                      <span>{tab.label}</span>
                      <span
                        className={`cht-px-2 cht-py-0.5 cht-rounded-full cht-text-[10px] cht-font-black ${
                          isActive ? "cht-bg-white/20 cht-text-white" : "cht-bg-gray-200 cht-text-gray-700"
                        }`}
                      >
                        {tab.count}
                      </span>
                    </button>
                  );
                })}
              </nav>
            </div>

            {/* Info Card */}
            <div className="cht-bg-gradient-to-br cht-from-[#3e57da] cht-to-[#1e293b] cht-p-6 cht-rounded-[28px] cht-text-white cht-shadow-xl cht-shadow-[#3e57da]/15 cht-relative cht-overflow-hidden">
              <div className="cht-relative cht-z-10 cht-space-y-3">
                <div className="cht-w-10 cht-h-10 cht-bg-white/15 cht-rounded-xl cht-flex cht-items-center cht-justify-center">
                  <Sparkles size={20} className="cht-text-amber-300" />
                </div>
                <h4 className="cht-text-base cht-font-black">Universal Activity Center</h4>
                <p className="cht-text-xs cht-text-white/80 cht-leading-relaxed">
                  Real-time alerts, system announcements, and module interactions are unified across your account.
                </p>
              </div>
              <div className="cht-absolute -cht-right-6 -cht-bottom-6 cht-w-32 cht-h-32 cht-bg-white/5 cht-rounded-full cht-blur-2xl" />
            </div>
          </aside>
        </div>
      </main>

      {/* View Notification Dossier Modal */}
      <AnimatePresence>
        {selectedNotification && (
          <div className="cht-fixed cht-inset-0 cht-z-[10000] cht-flex cht-items-center cht-justify-center cht-p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="cht-absolute cht-inset-0 cht-bg-slate-950/50 cht-backdrop-blur-sm"
              onClick={() => setSelectedNotification(null)}
            />

            <motion.div
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96, y: 16 }}
              transition={{ type: "spring", stiffness: 350, damping: 28 }}
              className="cht-relative cht-bg-white cht-w-full cht-max-w-xl cht-rounded-[32px] cht-p-8 cht-shadow-2xl cht-border cht-border-gray-100 cht-space-y-6"
            >
              {/* Modal Header */}
              <div className="cht-flex cht-items-start cht-justify-between cht-pb-4 cht-border-b cht-border-gray-100">
                <div className="cht-space-y-2">
                  <div className="cht-flex cht-items-center cht-gap-2">
                    <span
                      className={`cht-px-3 cht-py-1 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-wider ${
                        getTypeConfig(selectedNotification.notification_type).badgeBg
                      }`}
                    >
                      {getTypeConfig(selectedNotification.notification_type).label}
                    </span>
                    {selectedNotification.target_model && (
                      <span className="cht-inline-flex cht-items-center cht-gap-1 cht-px-2.5 cht-py-0.5 cht-bg-gray-100 cht-text-gray-700 cht-rounded-full cht-text-[9px] cht-font-black cht-uppercase cht-tracking-wider">
                        <Layers size={10} />
                        {selectedNotification.target_model}
                        {selectedNotification.target_id ? ` #${selectedNotification.target_id}` : ""}
                      </span>
                    )}
                  </div>
                  <h3 className="cht-text-xl cht-font-black cht-text-gray-900">
                    {selectedNotification.title}
                  </h3>
                </div>

                <button
                  onClick={() => setSelectedNotification(null)}
                  className="cht-p-2 cht-bg-gray-50 hover:cht-bg-gray-100 cht-rounded-full cht-text-gray-500 cht-transition-colors"
                  aria-label="Close"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Message Body */}
              <div className="cht-space-y-2">
                <h5 className="cht-text-[10px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                  Notification Message
                </h5>
                <p className="cht-text-sm cht-text-gray-700 cht-font-medium cht-leading-relaxed cht-whitespace-pre-line">
                  {selectedNotification.message}
                </p>
              </div>

              {/* Metadata Grid */}
              <div className="cht-grid cht-grid-cols-2 cht-gap-3 cht-bg-gray-50 cht-p-4 cht-rounded-2xl cht-border cht-border-gray-100">
                <div className="cht-space-y-1">
                  <span className="cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Timestamp
                  </span>
                  <div className="cht-text-xs cht-font-bold cht-text-gray-800 cht-flex cht-items-center cht-gap-1.5">
                    <Clock size={12} className="cht-text-gray-400" />
                    {new Date(selectedNotification.created_at).toLocaleString("en-US", {
                      month: "short",
                      day: "numeric",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </div>
                </div>

                <div className="cht-space-y-1">
                  <span className="cht-text-[9px] cht-font-black cht-text-gray-400 cht-uppercase cht-tracking-widest">
                    Triggered By
                  </span>
                  <div className="cht-text-xs cht-font-bold cht-text-gray-800 cht-flex cht-items-center cht-gap-1.5">
                    <User size={12} className="cht-text-gray-400" />
                    {selectedNotification.actor_name || "Platform Automated Event"}
                  </div>
                </div>
              </div>

              {/* Actions */}
              <div className="cht-flex cht-flex-col sm:cht-flex-row cht-gap-3 cht-border-t cht-border-gray-100 cht-pt-4">
                {(selectedNotification.extra_data?.route ||
                  selectedNotification.extra_data?.url ||
                  selectedNotification.extra_data?.link) && (
                  <Button
                    variant="primary"
                    className="cht-flex-1"
                    leftIcon={<ExternalLink size={15} />}
                    onClick={() => {
                      const link =
                        selectedNotification.extra_data?.route ||
                        selectedNotification.extra_data?.url ||
                        selectedNotification.extra_data?.link;
                      setSelectedNotification(null);
                      if (link.startsWith("http://") || link.startsWith("https://")) {
                        window.open(link, "_blank");
                      } else {
                        navigate(link);
                      }
                    }}
                  >
                    Open Associated Details
                  </Button>
                )}
                <Button
                  variant="outline"
                  className="cht-flex-1"
                  onClick={() => setSelectedNotification(null)}
                >
                  Dismiss
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default UniversalNotificationsPage;
